import { useEffect, useState } from "react";
import { api } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";
import { painLabel, desireLabel, objectionLabel, type InsightInputs, type InsightOutput } from "@shared/insight.ts";

const FIELDS: { key: keyof InsightInputs; label: string }[] = [
  { key: "product", label: "สินค้า/บริการ" },
  { key: "customerType", label: "ประเภทลูกค้า" },
  { key: "ageRange", label: "ช่วงอายุ" },
  { key: "profession", label: "อาชีพ" },
  { key: "lifestyle", label: "ไลฟ์สไตล์" },
  { key: "price", label: "ราคา/กำลังซื้อ" },
];

export default function AudienceInsight() {
  const { activeBrand } = useBrands();
  const [inp, setInp] = useState<InsightInputs>({});
  const [out, setOut] = useState<InsightOutput | null>(null);
  const [isMock, setIsMock] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => { load(); }, [activeBrand?.id]);
  async function load() { if (!activeBrand) return setHistory([]); const r = await api.get<{ personas: any[] }>(`/insight?brandId=${activeBrand.id}`); setHistory(r.personas); }
  const set = (k: keyof InsightInputs, v: string) => setInp((p) => ({ ...p, [k]: v }));

  async function generate() {
    if (!activeBrand) { setErr("เลือกแบรนด์ก่อน"); return; }
    setErr(null); setLoading(true); setOut(null); setSaved(false);
    try {
      const r = await api.post<any>("/insight/generate", { brandId: activeBrand.id, inputs: inp });
      if (r.error === "ai_unparsable") { setErr("AI ตอบไม่เป็นรูปแบบ ลองใหม่"); return; }
      setOut(r.output); setIsMock(r.isMock); load();
    } catch (e: any) { setErr("ผิดพลาด: " + (e?.message || "")); }
    finally { setLoading(false); }
  }
  async function open(id: string) { setLoading(true); try { const r = await api.get<{ persona: any }>(`/insight/${id}`); setOut(r.persona.data); setIsMock(false); } finally { setLoading(false); } }
  async function saveLearning() {
    if (!activeBrand || !out) return;
    await api.post("/learnings", { brandId: activeBrand.id, type: "audience", title: `Persona: ${out.persona.name}`, situation: out.persona.summary, sourceModule: "audience", confidence: "medium" });
    setSaved(true);
  }

  const group = <T extends { category: string }>(arr: T[]) => {
    const m: Record<string, T[]> = {};
    for (const x of arr) (m[x.category] ??= []).push(x);
    return m;
  };

  return (
    <div className="page">
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">วิเคราะห์กลุ่มเป้าหมาย <span className="mh-muted mh-eng" style={{ fontSize: 15 }}>Customer Insight</span></div>
          <div className="page__subtitle">{activeBrand ? <>บริบท: <b>{activeBrand.name}</b> — ขุด Pain/Desire/Objection + Persona + Message Map</> : "เลือกแบรนด์ก่อน"}</div>
        </div>
        <Icon name="Users" size={22} />
      </div>

      <div className="mh-card card-pad" style={{ marginBottom: 18 }}>
        <div className="mh-help" style={{ marginBottom: 10 }}>ช่องว่างดึงจาก Brand Brain อัตโนมัติ</div>
        <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))" }}>
          {FIELDS.map((f) => (
            <div className="mh-field" style={{ margin: 0 }} key={f.key}>
              <label className="mh-label" style={{ fontSize: 12.5 }}>{f.label}</label>
              <input className="mh-input" value={(inp[f.key] as string) || ""} onChange={(e) => set(f.key, e.target.value)} />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={generate} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Icon name="Users" size={18} /> ขุด Insight</>}
          </button>
          {history.length > 0 && (
            <select className="mh-select" style={{ width: "auto" }} defaultValue="" onChange={(e) => e.target.value && open(e.target.value)}>
              <option value="">📁 Persona เดิม ({history.length})</option>
              {history.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          )}
          {err && <span className="mh-pill mh-pill--problem">{err}</span>}
        </div>
      </div>

      {loading && <div className="empty"><span className="spinner spinner--dark" /><p className="mh-sec">กำลังขุด Insight...</p></div>}

      {out && (
        <>
          {isMock && <div className="mock-banner"><Icon name="Wrench" size={18} /><div>Insight จาก <b>Mock</b> — ใส่ Claude Key เพื่อ Insight ที่ลึกและเฉพาะแบรนด์จริง</div></div>}

          <div className="mh-card card-pad" style={{ marginBottom: 14 }}>
            <div className="mh-spread">
              <div style={{ flex: 1 }}>
                <div className="mh-muted" style={{ fontSize: 12.5 }}>Persona</div>
                <div style={{ fontWeight: 800, fontSize: 19 }}>{out.persona.name}</div>
                <div className="mh-sec" style={{ marginTop: 4 }}>{out.persona.summary}</div>
              </div>
              <button className="mh-btn mh-btn--subtle mh-btn--sm" onClick={saveLearning} disabled={saved}>{saved ? "บันทึกแล้ว ✓" : <><Icon name="Save" size={15} /> Save</>}</button>
            </div>
            <div className="form-grid" style={{ marginTop: 12, gap: "6px 16px" }}>
              <div><span className="mh-muted" style={{ fontSize: 12.5 }}>ข้อมูลประชากร: </span>{out.persona.demographics}</div>
              <div><span className="mh-muted" style={{ fontSize: 12.5 }}>จิตวิทยา: </span>{out.persona.psychographics}</div>
              <div style={{ gridColumn: "1 / -1" }}><span className="mh-muted" style={{ fontSize: 12.5 }}>วันหนึ่งของเขา: </span>{out.persona.aDayInLife}</div>
            </div>
          </div>

          <div className="form-grid" style={{ alignItems: "start" }}>
            <GroupCard title="😣 Pain (แยกหมวด)" tone="problem" groups={group(out.pains)} label={painLabel} render={(p: any) => <>{p.text} {p.intensity && <span className="mh-muted">· ระดับ {p.intensity}/5</span>}</>} />
            <GroupCard title="✨ Desire (แยกหมวด)" tone="winner" groups={group(out.desires)} label={desireLabel} render={(x: any) => x.text} />
          </div>

          <div className="mh-card card-pad" style={{ margin: "14px 0" }}>
            <h3 style={{ fontSize: 14, marginBottom: 12 }}>🛡️ Objection → วิธีแก้ → Section</h3>
            <div style={{ display: "grid", gap: 10 }}>
              {out.objections.map((o, i) => (
                <div key={i} style={{ borderBottom: "1px solid var(--border)", paddingBottom: 10 }}>
                  <div className="mh-row" style={{ gap: 8 }}><span className="mh-pill mh-pill--neutral">{objectionLabel(o.category)}</span><b>{o.text}</b></div>
                  <div className="mh-sec" style={{ fontSize: 13.5, marginTop: 4 }}>✅ แก้ด้วย: {o.counter} <span className="mh-pill mh-pill--testing" style={{ marginLeft: 6 }}>{o.mappedSection}</span></div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-grid" style={{ alignItems: "start" }}>
            <div className="mh-card card-pad">
              <h3 style={{ fontSize: 14, marginBottom: 10 }}>⚡ Buying Triggers</h3>
              <ul style={{ margin: 0, paddingLeft: 18 }}>{out.buyingTriggers.map((b, i) => <li key={i} style={{ marginBottom: 6 }}>{b}</li>)}</ul>
            </div>
            <div className="mh-card card-pad">
              <h3 style={{ fontSize: 14, marginBottom: 10 }}>🗺️ Message Map</h3>
              <div style={{ display: "grid", gap: 8 }}>
                {out.messageMap.map((m, i) => (
                  <div key={i} style={{ fontSize: 13.5 }}>
                    <b>{m.audienceState}</b><br />
                    <span className="mh-sec">→ {m.coreMessage}</span> <span className="mh-muted">({m.proof})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function GroupCard({ title, tone, groups, label, render }: { title: string; tone: string; groups: Record<string, any[]>; label: (id: string) => string; render: (x: any) => React.ReactNode }) {
  return (
    <div className="mh-card card-pad">
      <h3 style={{ fontSize: 14, marginBottom: 12 }}>{title}</h3>
      <div style={{ display: "grid", gap: 10 }}>
        {Object.entries(groups).map(([cat, items]) => (
          <div key={cat}>
            <span className={"mh-pill mh-pill--" + tone} style={{ marginBottom: 4, display: "inline-block" }}>{label(cat)}</span>
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{items.map((x, i) => <li key={i} style={{ fontSize: 13.5, marginBottom: 3 }}>{render(x)}</li>)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
}
